import { CheckboxGroup } from '@components/checkbox-group';
import { withCustomConditionOption, withCustomOption } from '@components/form/v2';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconCheckmark } from '@components/icons/checkmark';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';
import { useOptionsFieldActions } from './use-field-actions';
import { useFieldOptions } from './use-field-options';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import {
  ChoiceGroupWidthFix,
  MAPPED_OPTION_VALUE,
  SelectDefaultValueMapping,
  useMappedDefaultPill,
} from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { optionsTab } from '../options-tab';
import { RequiredSwitch } from '../required-switch';
import { StaleConditionWarningIcon } from '../stale-condition-warning';
import { ValueMenu } from '../variable-condition-menus';

type Config = FieldConfig<'input-multiple-choice'>;

const Display: Config['Display'] = ({ field }) => {
  const options = withCustomOption(field, useFieldOptions(field));
  const mappedPill = useMappedDefaultPill(field);

  return (
    <ChoiceGroupWidthFix>
      <CheckboxGroup
        label={asteriskLabel(field.label, field.isRequired)}
        description={field.description}
        value={mappedPill ? [MAPPED_OPTION_VALUE] : field.defaultValue}
        labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
        labelIconAfter={<StaleConditionWarningIcon field={field} />}
      >
        {[
          ...(mappedPill
            ? [
                <CheckboxGroup.Item
                  key={MAPPED_OPTION_VALUE}
                  label={mappedPill}
                  value={MAPPED_OPTION_VALUE}
                />,
              ]
            : []),
          ...options.map((option) => (
            <CheckboxGroup.Item key={option.value} label={option.label} value={option.value} />
          )),
        ]}
      </CheckboxGroup>
    </ChoiceGroupWidthFix>
  );
};

const FormBuilderMultipleChoice: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;
  const menuActions = useOptionsFieldActions(field);

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        menu={menuActions}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <RequiredSwitch field={field} />
            <SelectDefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Multiple choice',
    label: 'Label',
    default: 'Default selected options',
  },
});

export const multipleChoiceConfig: Config = {
  kind: 'input-multiple-choice',
  title: t('title'),
  group: 'selections',
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Icon: IconCheckmark,
  Component: FormBuilderMultipleChoice,
  propertiesTabs: [optionsTab, conditionsTab],
  condition: {
    comparators: ['is', 'not_is', 'any_of', 'none_of'],
    ValueMenu: ({ field, ...rest }) => {
      const options = withCustomConditionOption(field, useFieldOptions(field));
      return <ValueMenu options={options} {...rest} />;
    },
  },
};
